import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, RotateCcw, X, Clock, AlertTriangle } from "lucide-react";
import { api, formatTL } from "@/lib/api";

/**
 * Prim Çöp Kutusu modal (admin only).
 *
 * Lists prim rows soft-deleted in the last 24h and lets admins restore any
 * (or multiple) back into the live prim collection. TTL on backend auto-purges
 * entries after 24h; admins may also permanently purge immediately.
 */
export default function PrimTrashModal({ onClose, onRestored }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/prim/trash");
      setRows(Array.isArray(r.data?.rows) ? r.data.rows : []);
      setSelected(new Set());
    } catch (e) {
      toast.error("Çöp kutusu yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (pid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.prim_id))));
  };

  const restoreOne = async (pid) => {
    setBusy(true);
    try {
      await api.post(`/prim/trash/${pid}/restore`);
      toast.success("Prim geri yüklendi");
      load();
      onRestored?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Geri yüklenemedi");
    } finally {
      setBusy(false);
    }
  };

  const restoreSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const r = await api.post("/prim/trash/bulk-restore", { ids });
      toast.success(`${r.data?.restored || 0} prim geri yüklendi${r.data?.skipped ? ` · ${r.data.skipped} atlandı` : ""}`);
      load();
      onRestored?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toplu geri yükleme başarısız");
    } finally {
      setBusy(false);
    }
  };

  const purgeOne = async (pid) => {
    if (!window.confirm("Bu kayıt KALICI olarak silinecek. Geri alınamaz. Devam?")) return;
    setBusy(true);
    try {
      await api.delete(`/prim/trash/${pid}`);
      toast.success("Çöp kutusundan kalıcı silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    } finally {
      setBusy(false);
    }
  };

  const timeLeft = (expIso) => {
    try {
      const ms = new Date(expIso).getTime() - Date.now();
      if (ms <= 0) return "süresi dolmuş";
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      return `${h}s ${m}dk kaldı`;
    } catch { return "—"; }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="prim-trash-modal">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-2 bg-slate-50">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-rose-100 text-rose-700 grid place-items-center">
              <Trash2 size={18} />
            </div>
            <div>
              <h3 className="font-display text-base font-bold text-slate-900">Prim Çöp Kutusu</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Silinen primler 24 saat burada tutulur · süresi dolanlar otomatik kalıcı silinir
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="text-center py-10 text-slate-400 text-sm">Yükleniyor...</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">
              Çöp kutusu boş · Son 24 saatte silinen prim yok
            </div>
          ) : (
            <>
              {selected.size > 0 && (
                <div className="mb-3 flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-900">
                  <div><strong>{selected.size}</strong> prim seçildi</div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="trash-bulk-restore"
                      onClick={restoreSelected}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md font-semibold disabled:opacity-50"
                    >
                      <RotateCcw size={13} /> Seçilenleri Geri Yükle ({selected.size})
                    </button>
                    <button
                      onClick={() => setSelected(new Set())}
                      className="text-emerald-700 hover:underline font-semibold px-2 py-1.5"
                    >Temizle</button>
                  </div>
                </div>
              )}
              <table className="w-full text-sm" data-testid="trash-table">
                <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="px-2 py-2 text-center w-8">
                      <input
                        type="checkbox"
                        checked={rows.length > 0 && selected.size === rows.length}
                        onChange={toggleAll}
                        data-testid="trash-select-all"
                      />
                    </th>
                    <th className="px-3 py-2 text-left">Silinme</th>
                    <th className="px-3 py-2 text-left">Kalan</th>
                    <th className="px-3 py-2 text-left">İcra Föy</th>
                    <th className="px-3 py-2 text-left">İcra Esas</th>
                    <th className="px-3 py-2 text-left">Alacaklı</th>
                    <th className="px-3 py-2 text-left">Dosya</th>
                    <th className="px-3 py-2 text-right">Vekalet</th>
                    <th className="px-3 py-2 text-right">Tahsilat</th>
                    <th className="px-3 py-2 text-left">Silen</th>
                    <th className="px-3 py-2 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const checked = selected.has(r.prim_id);
                    return (
                      <tr
                        key={r.prim_id}
                        data-testid={`trash-row-${r.prim_id}`}
                        className={`border-t border-slate-100 hover:bg-slate-50 ${checked ? "bg-indigo-50" : ""}`}
                      >
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(r.prim_id)}
                            data-testid={`trash-check-${r.prim_id}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 font-mono-tab whitespace-nowrap">
                          {r.trashed_at?.slice(0, 16).replace("T", " ")}
                        </td>
                        <td className="px-3 py-2 text-xs text-amber-700 font-semibold whitespace-nowrap">
                          <Clock size={10} className="inline mr-1" />
                          {timeLeft(r.expires_at)}
                        </td>
                        <td className="px-3 py-2 text-xs">{r.icra_foy || "—"}</td>
                        <td className="px-3 py-2 text-xs">{r.icra_esas || "—"}</td>
                        <td className="px-3 py-2 text-xs">{r.alacakli_adi || "—"}</td>
                        <td className="px-3 py-2 text-xs truncate max-w-[150px]" title={r.dosya_adi}>{r.dosya_adi || "—"}</td>
                        <td className="px-3 py-2 text-right text-xs font-mono-tab">{formatTL(r.vekalet_ucreti || 0)}</td>
                        <td className="px-3 py-2 text-right text-xs font-mono-tab">{formatTL(r.tahsilat_miktari || 0)}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{r.trashed_by_email || "—"}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex gap-1">
                            <button
                              data-testid={`trash-restore-${r.prim_id}`}
                              onClick={() => restoreOne(r.prim_id)}
                              disabled={busy}
                              className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded text-[10px] font-semibold disabled:opacity-50"
                              title="Primi aktif listeye geri yükle"
                            >
                              <RotateCcw size={11} /> Geri Yükle
                            </button>
                            <button
                              data-testid={`trash-purge-${r.prim_id}`}
                              onClick={() => purgeOne(r.prim_id)}
                              disabled={busy}
                              className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white px-2 py-1 rounded text-[10px] font-semibold disabled:opacity-50"
                              title="Çöp kutusundan KALICI olarak sil"
                            >
                              <Trash2 size={11} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-3 text-[10px] text-slate-400 flex items-center gap-1">
                <AlertTriangle size={11} /> Süresi dolan kayıtlar MongoDB TTL ile otomatik silinir (max dakikada bir tarama).
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
